import { useState, useEffect } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { api } from '../../../api/client';
import { Can } from '../../../lib/rbac';

interface ReadyOrder {
  order_id: number;
  ml_order_id: string;
  seller_id: number;
  buyer_nickname: string | null;
  ml_shipment_id: number | null;
  ship_status: string;
}

export interface LeftoverSearchModalProps {
  asin: string;
  onClose: () => void;
  onAssociated: () => void;
}

export function LeftoverSearchModal({ asin, onClose, onAssociated }: LeftoverSearchModalProps) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ReadyOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(
        `/warehouse/leftover/search-ready-orders?asin=${encodeURIComponent(asin)}` +
          (q.trim() ? `&q=${encodeURIComponent(q.trim())}` : '')
      )
      .then((res: any) => {
        if (!cancelled) setResults(res.success ? res.results : []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [asin, q]);

  const associate = async (orderId: number) => {
    setBusyId(orderId);
    setError(null);
    try {
      const res: any = await api.post('/warehouse/leftover/associate-ready-order', {
        order_id: orderId,
      });
      if (!res.success) throw new Error(res.error || 'Error al asociar la venta');
      onAssociated();
    } catch (err: any) {
      setError(err?.message || 'Error al asociar la venta');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-bg-card)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
          <span className="font-bold text-[var(--color-text)]">
            Buscar venta lista para despachar
          </span>
          <button onClick={onClose}>
            <X size={20} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="flex items-center gap-2 border border-[var(--color-border)] rounded-lg px-3 py-2">
            <Search size={16} className="text-[var(--color-text-secondary)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por comprador o nº de orden (opcional)"
              className="flex-1 bg-transparent outline-none text-sm text-[var(--color-text)]"
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[var(--color-text-secondary)]" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)] text-center py-8">
              No hay ventas listas para despachar de este producto.
            </p>
          ) : (
            results.map((o) => (
              <div
                key={o.order_id}
                className="flex items-center justify-between bg-[var(--color-bg)] rounded-lg p-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-text)]">{o.ml_order_id}</p>
                  <p className="text-xs text-[var(--color-text-secondary)] truncate">
                    {o.buyer_nickname || 'sin comprador'} · envío {o.ship_status}
                  </p>
                </div>
                <Can cap="warehouse.operate">
                  <button
                    disabled={busyId !== null}
                    onClick={() => associate(o.order_id)}
                    className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {busyId === o.order_id ? 'Asociando…' : 'Asociar'}
                  </button>
                </Can>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
